'use client';

import Link from 'next/link';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Clock3, Feather, Leaf, LockKeyhole, Mail, Pause, RotateCcw, ShieldCheck } from 'lucide-react';

import {
  BEHAVIOR_ASSESSMENT_ESTIMATED_MINUTES,
  BEHAVIOR_ASSESSMENT_ITEM_COUNT,
  BEHAVIOR_ASSESSMENT_VERSION,
  BEHAVIOR_QUESTIONS,
  BEHAVIOR_RESPONSE_OPTIONS,
} from '@/lib/assessment/behavior-assessment';
import { useAppAuth } from '@/hooks/use-app-auth';
import { ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { DRAFT_TTL, LEGACY_DRAFT_KEY, openDraftScope, readScopedDraft, saveScopedDraft, prepareGuestHandoff, finishGuestHandoff, type AssessmentDraft, type DraftScope } from '@/lib/assessment/assessment-draft-client';
function answeredCountForHandoff(answers: Record<string, number>) { return Object.keys(answers).length > 0; }

const PAGE_SIZE = 10;
const PAGE_COUNT = Math.ceil(BEHAVIOR_QUESTIONS.length / PAGE_SIZE);


type AssessmentPhase = 'intro' | 'questions' | 'contact' | 'complete';

type SubmissionResult = {
  assessmentId: string;
  claimed: boolean;
  emailDelivery: { status: string };
};

export function BehaviorAssessmentClient() {
  const { user, session, loading } = useAppAuth();
  const uid = session?.uid || user?.uid || null;
  const owner = uid ? `account:${uid}` : 'guest';
  const [scope, setScope] = useState<DraftScope | null>(null);
  const conflicted = Boolean(user && session && user.uid !== session.uid);
  useEffect(() => {
    if (!loading && !conflicted) {
      try { setScope(openDraftScope(window.sessionStorage, uid)); }
      catch { setScope({ owner, key: crypto.randomUUID(), recoverFrom: null, persistent: false }); }
    }
  }, [loading, conflicted, uid, owner]);
  if (loading || conflicted || !scope || scope.owner !== owner) return <AssessmentLoading />;
  return <ParticipantAssessment key={scope.key} scope={scope} />;
}

function ParticipantAssessment({ scope }: { scope: DraftScope }) {
  const { user, profile, session, loading: authLoading } = useAppAuth();
  const [phase, setPhase] = useState<AssessmentPhase>('intro');
  const [hydrated, setHydrated] = useState(false);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [pageIndex, setPageIndex] = useState(0);
  const [activeSeconds, setActiveSeconds] = useState(0);
  const [adultConfirmed, setAdultConfirmed] = useState(false);
  const [profilingConsent, setProfilingConsent] = useState(false);
  const [researchConsent, setResearchConsent] = useState(false);
  const [respondentName, setRespondentName] = useState('');
  const [email, setEmail] = useState('');
  const [age, setAge] = useState('');
  const [podcastContactConsent, setPodcastContactConsent] = useState(false);
  const [podcastContactPreference, setPodcastContactPreference] = useState<'email' | 'phone'>('email');
  const [podcastPhone, setPodcastPhone] = useState('');
  const [podcastStory, setPodcastStory] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [website, setWebsite] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submission, setSubmission] = useState<SubmissionResult | null>(null);
  const pendingKey = `${scope.key}:submission`;
  const alive = useRef(true);
  const epoch = useRef(0);
  const [saveStatus, setSaveStatus] = useState(scope.persistent ? 'Local-only: waiting to save in this tab.' : 'Save failed: storage unavailable. Answers are only in memory.');
  const [legacy, setLegacy] = useState(false);
  const [legacyAttempt, setLegacyAttempt] = useState(false);
  const [handoff, setHandoff] = useState(scope.recoverFrom);
  useLayoutEffect(() => { alive.current = true; const generation = epoch; return () => { alive.current = false; generation.current++; }; }, []);
  const firstUnansweredRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let draft: AssessmentDraft | null = null;
    try { draft = readScopedDraft(window.sessionStorage, scope.key); if (!draft && window.sessionStorage.getItem(scope.key)) setError('The saved draft has expired or is invalid. Start fresh; no answers or consent were restored.'); } catch { setSaveStatus('Save failed: storage unavailable. Answers are only in memory.'); }
    try { setLegacy(Boolean(window.localStorage.getItem(LEGACY_DRAFT_KEY))); } catch { /* No legacy storage access. */ }
    if (draft) {
      setAnswers(draft.answers);
      setPageIndex(Math.max(0, Math.min(PAGE_COUNT - 1, draft.pageIndex || 0)));
      setActiveSeconds(Math.max(0, draft.activeSeconds || 0));
      setAdultConfirmed(Boolean(draft.adultConfirmed));
      setProfilingConsent(Boolean(draft.profilingConsent));
      setResearchConsent(Boolean(draft.researchConsent));
      if (draft.started) {
        setPhase(Object.keys(draft.answers).length === BEHAVIOR_ASSESSMENT_ITEM_COUNT ? 'contact' : 'questions');
      }
    }
    try { setLegacyAttempt(scope.owner.startsWith('account:') && Boolean(window.sessionStorage.getItem(`${LEGACY_DRAFT_KEY}:submission:${scope.owner.slice(8)}`))); } catch { /* Legacy storage remains untouched. */ }
    setHydrated(true);
  }, [scope.key, scope.owner]);

  useEffect(() => {
    if (authLoading) return;
    setSubmission(null);
    try {
      const envelope = JSON.parse(window.sessionStorage.getItem(pendingKey) || 'null');
      const pending = envelope?.key === scope.key && envelope?.expiresAt > Date.now() ? envelope.body : null;
      if (pending?.submissionKey && pending.answers) {
        setAnswers(pending.answers); setActiveSeconds(pending.durationSeconds);
        setRespondentName(pending.respondentName); setEmail(pending.email); setAge(String(pending.age));
        setAdultConfirmed(pending.adultConfirmed); setProfilingConsent(pending.profilingConsent); setResearchConsent(pending.researchConsent);
        setMarketingConsent(pending.marketingConsent); setPodcastContactConsent(pending.podcastContactConsent);
        setPodcastContactPreference(pending.podcastContactPreference); setPodcastPhone(pending.podcastPhone); setPodcastStory(pending.podcastStory);
        setPhase('contact');
        setError('A previous submission may already be saved. Submit again to recover it safely. Use Start over only for a new assessment.');
      }
    } catch { /* Submission itself requires working recovery storage before any request. */ }
  }, [authLoading, pendingKey, scope.key]);

  useEffect(() => {
    if (!user && !session) return;
    setEmail((current) => current || user?.email || session?.email || '');
    setRespondentName((current) => current || (profile?.uid === (session?.uid || user?.uid) ? profile?.displayName : '') || user?.displayName || session?.displayName || '');
  }, [profile?.displayName, profile?.uid, user, session]);

  const draftSnapshot = useRef<AssessmentDraft | null>(null);
  useEffect(() => {
    if (hydrated && phase !== 'complete') setSaveStatus('Local-only: changes awaiting the next save in this tab.');
    draftSnapshot.current = hydrated && phase !== 'complete' ? {
      version: BEHAVIOR_ASSESSMENT_VERSION, answers, pageIndex, activeSeconds,
      started: phase !== 'intro', adultConfirmed, profilingConsent, researchConsent,
    } : null;
  }, [activeSeconds, adultConfirmed, answers, hydrated, pageIndex, phase, profilingConsent, researchConsent]);
  useEffect(() => {
    let last = '';
    const flush = () => {
      if (!alive.current || !draftSnapshot.current) return;
      const next = JSON.stringify(draftSnapshot.current);
      if (next === last) return;
      try {
        saveScopedDraft(window.sessionStorage, scope.key, draftSnapshot.current);
        last = next;
        setSaveStatus('Saved locally in this tab only. Draft expires after 7 days; closing the tab can remove it.');
      } catch { setSaveStatus('Save failed: storage unavailable. Answers are only in memory.'); }
    };
    const timer = window.setInterval(flush, 1500);
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', flush);
    return () => {
      if (draftSnapshot.current) { try { saveScopedDraft(window.sessionStorage, scope.key, draftSnapshot.current); } catch { /* Memory cannot persist after leaving. */ } }
      clearInterval(timer); window.removeEventListener('pagehide', flush); document.removeEventListener('visibilitychange', flush); };
  }, [scope.key]);

  function recoverGuest() {
    try {
      const draft = handoff ? readScopedDraft(window.sessionStorage, handoff) : null;
      if (!draft) throw new Error('That guest draft expired or is unavailable.');
      // Transfer only answers/timing after an explicit choice. Consent is always confirmed anew.
      setAnswers(draft.answers); setPageIndex(draft.pageIndex); setActiveSeconds(draft.activeSeconds);
      setAdultConfirmed(false); setProfilingConsent(false); setResearchConsent(false); setPhase('intro');
      finishGuestHandoff(window.sessionStorage); setHandoff(null);
    } catch { setError('Guest recovery unavailable. The original draft has been preserved.'); }
  }
  const recoveryNotice = <div className="mb-5 space-y-2 text-xs text-[#665f58]">
    <p role="status">{saveStatus}</p>
    {legacy && <p>A legacy draft is preserved on this device. Its participant cannot be verified, so its answers and consent are not opened here. You can continue a scoped draft or start fresh without deleting it.</p>}
    {legacyAttempt && phase === 'intro' && !answeredCountForHandoff(answers) && <button type="button" className="underline" onClick={() => {
      try {
        const body = JSON.parse(window.sessionStorage.getItem(`${LEGACY_DRAFT_KEY}:submission:${scope.owner.slice(8)}`) || 'null');
        if (!body?.submissionKey || !body.answers || window.sessionStorage.getItem(pendingKey)) throw new Error();
        window.sessionStorage.setItem(pendingKey, JSON.stringify({ key: scope.key, expiresAt: Date.now() + DRAFT_TTL, body }));
        window.location.reload();
      } catch { setError('The previous account attempt could not be recovered. Its original storage is preserved.'); }
    }}>Recover my previous account submission attempt</button>}
    {handoff && <div><p>A guest draft was explicitly prepared for sign-in in this tab. Continue only if you prepared it yourself.</p><button type="button" onClick={recoverGuest} className="underline">Continue my prepared guest draft</button><button type="button" onClick={() => { try { finishGuestHandoff(window.sessionStorage); } catch { /* Preserved but not displayed. */ } setHandoff(null); }} className="ml-4 underline">Keep separate</button></div>}
    {scope.owner === 'guest' && answeredCountForHandoff(answers) && <button type="button" className="underline" onClick={() => {
      try { if (draftSnapshot.current) saveScopedDraft(window.sessionStorage, scope.key, draftSnapshot.current); prepareGuestHandoff(window.sessionStorage, scope); setError('Draft prepared for sign-in in this tab for 10 minutes. After signing in, choose Continue my prepared guest draft. Consent must be confirmed again.'); }
      catch (error) { setError(error instanceof Error ? error.message : 'Unable to prepare guest continuation.'); }
    }}>Prepare this guest draft for sign-in</button>}
  </div>;

  useEffect(() => {
    if (phase !== 'questions') return;
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') setActiveSeconds((seconds) => seconds + 1);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [phase]);

  const currentQuestions = useMemo(
    () => BEHAVIOR_QUESTIONS.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE),
    [pageIndex],
  );
  const answeredCount = Object.keys(answers).filter((id) => BEHAVIOR_QUESTIONS.some((question) => question.id === id)).length;
  const progress = Math.round((answeredCount / BEHAVIOR_QUESTIONS.length) * 100);
  const currentPageComplete = currentQuestions.every((question) => answers[question.id]);

  function beginAssessment() {
    if (!adultConfirmed || !profilingConsent) {
      setError('Confirm age eligibility and personal-processing consent before beginning.');
      return;
    }
    setError(null);
    setPhase('questions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function answer(questionId: string, value: number) {
    setAnswers((current) => ({ ...current, [questionId]: value }));
    setError(null);
  }

  function goNext() {
    if (!currentPageComplete) {
      const firstMissing = currentQuestions.find((question) => !answers[question.id]);
      setError('Please answer every statement on this page before continuing.');
      window.setTimeout(() => document.getElementById(`question-${firstMissing?.id}`)?.focus(), 0);
      return;
    }
    setError(null);
    if (pageIndex === PAGE_COUNT - 1) {
      setPhase('contact');
    } else {
      setPageIndex((index) => index + 1);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goBack() {
    setError(null);
    if (pageIndex === 0) setPhase('intro');
    else setPageIndex((index) => index - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function startOver() {
    if (!window.confirm('Start a new assessment and remove locally saved answers? A previously submitted report may already be saved; this starts a genuine retake.')) return;
    epoch.current++;
    draftSnapshot.current = null; setSubmitting(false); setSubmission(null);
    try { window.sessionStorage.removeItem(scope.key); window.sessionStorage.removeItem(pendingKey); } catch { setSaveStatus('Save failed: local recovery storage could not be cleared.'); return; }
    setAnswers({});
    setPageIndex(0);
    setActiveSeconds(0);
    setAdultConfirmed(false);
    setProfilingConsent(false);
    setResearchConsent(false);
    setAge(''); setRespondentName(''); setEmail(''); setWebsite('');
    setPodcastContactConsent(false);
    setPodcastContactPreference('email');
    setPodcastPhone('');
    setPodcastStory('');
    setMarketingConsent(false);
    setError(null);
    setPhase('intro');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function submitAssessment(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (authLoading) { setError('Wait for your account session to finish loading before submitting.'); return; }
    if (answeredCount !== BEHAVIOR_ASSESSMENT_ITEM_COUNT) {
      setError('The assessment is incomplete. Return to the unanswered statements before submitting.');
      return;
    }
    const requestEpoch = ++epoch.current;
    const valid = () => alive.current && requestEpoch === epoch.current;
    setSubmitting(true);
    setError(null);
    try {
      const envelope = JSON.parse(window.sessionStorage.getItem(pendingKey) || 'null');
      if (envelope && (envelope.key !== scope.key || envelope.expiresAt <= Date.now())) throw new Error('This saved attempt expired. Start over explicitly; an earlier report may already exist.');
      const prior = envelope?.body;
      const body = {
        submissionKey: prior?.submissionKey || crypto.randomUUID(),
        respondentName, email, answers, durationSeconds: prior?.durationSeconds ?? activeSeconds,
        adultConfirmed, profilingConsent, researchConsent, age: Number(age), podcastContactConsent,
        podcastContactPreference, podcastPhone, podcastStory, marketingConsent, website,
      };
      if (prior && JSON.stringify(prior) !== JSON.stringify(body)) {
        throw new Error('This attempt is bound to your earlier answers and contact details. Reload to restore them, or start over for a new assessment.');
      }
      // Persist the identity and exact input before the network boundary. A reload/response loss
      // recovers this attempt; changing its payload can never silently create a second report.
      try { window.sessionStorage.setItem(pendingKey, JSON.stringify({ key: scope.key, expiresAt: envelope?.expiresAt || Date.now() + DRAFT_TTL, body })); }
      catch { throw new Error('Submission has not started: enable session storage so a retry can safely recover your report.'); }
      const response = await fetch('/api/assessment/submit', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Assessment-Actor': scope.owner },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!valid()) return;
      if (!response.ok) throw new Error(payload?.error || 'No saved report was confirmed. Retry this submission to recover it safely.');
      if (!payload.assessmentId) throw new Error('No saved report was confirmed. Retry this submission.');
      setSubmission(payload as SubmissionResult);
      setPhase('complete');
      draftSnapshot.current = null;
      try { window.sessionStorage.removeItem(scope.key); } catch { /* Saved report remains authoritative. */ }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (submissionError) {
      if (!valid()) return;
      setError(submissionError instanceof Error ? submissionError.message : 'No saved report was confirmed. Retry this submission to recover it safely.');
    } finally {
      if (valid()) setSubmitting(false);
    }
  }

  if (!hydrated) return <AssessmentLoading />;
  if (phase === 'intro') return (
    <AssessmentFrame>
      {recoveryNotice}
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_370px] lg:items-start">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#b8735c]">The Untangled Life · private self-reflection</p>
          <h1 className="mt-5 max-w-[760px] font-headline text-[clamp(3.1rem,7vw,6.5rem)] leading-[.94] tracking-[-0.045em] text-[#2b2926]">
            See the pattern.<br />Keep your voice.
          </h1>
          <p className="mt-7 max-w-[680px] text-[17px] leading-8 text-[#554f49]">
            A thoughtful 120-statement assessment of how you handle self-trust, boundaries, uncertainty, reciprocity, repair, digital pressure, and the work of staying connected to yourself.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <IntroMetric icon={Clock3} label="Time" value={`${BEHAVIOR_ASSESSMENT_ESTIMATED_MINUTES}–28 min`} />
            <IntroMetric icon={Feather} label="Depth" value={`${BEHAVIOR_ASSESSMENT_ITEM_COUNT} reflections`} />
            <IntroMetric icon={LockKeyhole} label="Access" value="Owner/admin restricted" />
          </div>
        </div>
        <aside className="rounded-[1.35rem] border border-[#decbbb] bg-[#fffdf9]/88 p-6 shadow-[0_20px_70px_rgba(68,49,36,.09)] backdrop-blur">
          <p className="font-headline text-[1.65rem] text-[#2f2d2a]">Before you begin</p>
          <p className="mt-3 text-[13px] leading-6 text-[#665f58]">Receiving your personal report requires your name, email and a private account with that same verified email. We send a neutral access link; results appear only inside your account. Answer for your usual behaviour during the last six months.</p>
          <div className="mt-5 space-y-3">
            <ConsentCheck checked={adultConfirmed} onChange={setAdultConfirmed}>
              I confirm that I am 18 or older.
            </ConsentCheck>
            <ConsentCheck checked={profilingConsent} onChange={setProfilingConsent}>
              I explicitly consent to storing my name, email, exact age, all 120 answers, and calculated profile for private account/report access and authorised owner/admin review, including automated scoring, as explained in the <Link href="/assessment/privacy" className="font-semibold text-[#9b5d47] underline underline-offset-2">assessment privacy notice</Link>.
            </ConsentCheck>
            <ConsentCheck checked={researchConsent} onChange={setResearchConsent}>
              Optional: include my answers in overall and age-based aggregate statistics. Public statistics are anonymous aggregates; my identified private record is never public. I can withdraw this choice without losing my private report.
            </ConsentCheck>
          </div>
          {error ? <AssessmentError>{error}</AssessmentError> : null}
          <button type="button" onClick={beginAssessment} className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-[.6rem] bg-[#5f8769] px-5 text-[14px] font-semibold text-white shadow-[0_12px_28px_rgba(95,135,105,.24)] transition hover:bg-[#52775d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f5e43] focus-visible:ring-offset-2">
            Begin the assessment <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-4 text-[11px] leading-5 text-[#817870]">This is not a diagnostic, clinical, medical, legal, or crisis assessment. It does not determine whether another person has a disorder.</p>
        </aside>
      </div>
      {answeredCount ? (
        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#dfcbbb] bg-white/70 px-5 py-4 text-sm text-[#5b5550]">
          <span>This participant session has {answeredCount} answers. See the local save status above.</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPhase(answeredCount === BEHAVIOR_ASSESSMENT_ITEM_COUNT ? 'contact' : 'questions')} className="font-semibold text-[#52775d] underline underline-offset-4">Resume</button>
            <button type="button" onClick={startOver} className="font-semibold text-[#9b5d47] underline underline-offset-4">Remove draft</button>
          </div>
        </div>
      ) : null}
    </AssessmentFrame>
  );

  if (phase === 'questions') return (
    <AssessmentFrame compact>
      {recoveryNotice}
      <header className="sticky top-16 z-20 -mx-4 border-b border-[#e5d5c8] bg-[#f8f2ec]/94 px-4 pb-5 pt-2 backdrop-blur-md sm:-mx-8 sm:px-8 lg:-mx-10 lg:px-10">
        <div className="mx-auto flex max-w-[1120px] items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[.26em] text-[#b8735c]">Part {pageIndex + 1} of {PAGE_COUNT}</p>
            <h1 className="mt-2 font-headline text-[1.8rem] leading-none text-[#2f2d2a]">Answer from lived experience.</h1>
          </div>
          <p className="shrink-0 text-right text-[12px] font-semibold text-[#5d665f]">{answeredCount}/{BEHAVIOR_ASSESSMENT_ITEM_COUNT}<br /><span className="font-normal text-[#817870]">{progress}% complete</span></p>
        </div>
        <div className="mx-auto mt-4 h-1.5 max-w-[1120px] overflow-hidden rounded-full bg-[#e7d8cb]" aria-label={`${progress}% complete`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
          <div className="h-full rounded-full bg-[linear-gradient(90deg,#b8735c,#5f8769)] transition-[width] duration-500" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <div className="mx-auto mt-8 max-w-[1120px]">
        <div className="mb-5 flex items-center justify-between gap-4 text-[12px] text-[#746c65]">
          <span>How true has each statement been for you in the last six months?</span>
          <span className="hidden sm:inline">Your progress saves on this device.</span>
        </div>
        <div className="space-y-4">
          {currentQuestions.map((question, questionIndex) => {
            const selected = answers[question.id];
            const firstMissing = !selected && currentQuestions.slice(0, questionIndex).every((item) => answers[item.id]);
            return (
              <div key={question.id} ref={firstMissing ? firstUnansweredRef : undefined} className={cn('rounded-[1rem] border bg-[#fffdf9] p-5 shadow-[0_9px_30px_rgba(68,49,36,.04)] transition sm:p-6', selected ? 'border-[#d8cabd]' : 'border-[#e6d5c8]')}>
                <fieldset>
                  <legend id={`question-${question.id}`} tabIndex={-1} className="max-w-[840px] text-[15px] font-medium leading-7 text-[#36322f] outline-none sm:text-[16px]">
                    <span className="mr-3 font-headline text-[1.2rem] font-normal text-[#b8735c]">{pageIndex * PAGE_SIZE + questionIndex + 1}.</span>{question.text}
                  </legend>
                  <div className="mt-5 grid grid-cols-5 gap-1.5 sm:gap-2" role="radiogroup" aria-labelledby={`question-${question.id}`}>
                    {BEHAVIOR_RESPONSE_OPTIONS.map((option) => (
                      <label key={option.value} className={cn('group relative flex min-h-[66px] cursor-pointer flex-col items-center justify-center rounded-[.65rem] border px-1.5 py-2 text-center transition focus-within:ring-2 focus-within:ring-[#5f8769] focus-within:ring-offset-2', selected === option.value ? 'border-[#5f8769] bg-[#e8efe8] text-[#31563d]' : 'border-[#e6d8cc] bg-white text-[#716961] hover:border-[#b8c9b9] hover:bg-[#f5f8f3]')}>
                        <input type="radio" name={question.id} value={option.value} checked={selected === option.value} onChange={() => answer(question.id, option.value)} className="sr-only" />
                        <span className={cn('flex h-7 w-7 items-center justify-center rounded-full border text-[12px] font-bold', selected === option.value ? 'border-[#5f8769] bg-[#5f8769] text-white' : 'border-[#cfc0b4] bg-[#fffdf9]')}>{selected === option.value ? <Check className="h-4 w-4" /> : option.value}</span>
                        <span className="mt-1.5 hidden text-[10px] leading-3 sm:block">{option.label}</span>
                        <span className="mt-1 text-[9px] leading-3 sm:hidden">{option.value === 1 ? 'Never' : option.value === 5 ? 'Always' : option.value === 3 ? 'Sometimes' : option.value === 2 ? 'Rarely' : 'Often'}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
            );
          })}
        </div>
        {error ? <AssessmentError>{error}</AssessmentError> : null}
        <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={goBack} className="inline-flex h-11 items-center gap-2 rounded-[.55rem] border border-[#d8c7ba] bg-white px-5 text-[13px] font-semibold text-[#4d4843] hover:bg-[#fbf6f1]"><ArrowLeft className="h-4 w-4" /> Back</button>
          <div className="flex flex-wrap gap-2">
            <Link href="/" className="inline-flex h-11 items-center gap-2 rounded-[.55rem] px-4 text-[13px] font-semibold text-[#6b645e] hover:bg-white"><Pause className="h-4 w-4" /> Pause</Link>
            <button type="button" onClick={goNext} className="inline-flex h-11 items-center gap-2 rounded-[.55rem] bg-[#5f8769] px-6 text-[13px] font-semibold text-white shadow-[0_10px_22px_rgba(95,135,105,.2)] hover:bg-[#52775d]">{pageIndex === PAGE_COUNT - 1 ? 'Finish questions' : 'Continue'} <ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
        <button type="button" onClick={startOver} className="mx-auto mt-8 flex items-center gap-2 text-[11px] text-[#8d7f75] underline underline-offset-4"><RotateCcw className="h-3.5 w-3.5" /> Start over and remove saved answers</button>
      </div>
    </AssessmentFrame>
  );

  if (phase === 'contact') return (
    <AssessmentFrame>
      {recoveryNotice}
      <div className="mx-auto grid max-w-[1040px] gap-8 lg:grid-cols-[minmax(0,1fr)_430px] lg:items-start">
        <div className="pt-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#e8efe8] text-[#4f765a]"><Check className="h-6 w-6" /></div>
          <p className="mt-6 text-[11px] font-bold uppercase tracking-[.28em] text-[#b8735c]">All {BEHAVIOR_ASSESSMENT_ITEM_COUNT} reflections complete</p>
          <h1 className="mt-4 font-headline text-[clamp(3rem,6vw,5.2rem)] leading-[.98] tracking-[-.04em] text-[#2f2d2a]">Your answers have a shape.</h1>
          <p className="mt-6 max-w-[580px] text-[16px] leading-8 text-[#5b5550]">Add your name and email to calculate the private report. We will email a neutral secure access link. Your complete 12-part profile is available only inside a verified private account using this same email address.</p>
          <div className="mt-7 rounded-[1rem] border border-[#d7dfd2] bg-[#eef1e8] p-5">
            <p className="flex items-center gap-2 text-[13px] font-semibold text-[#34513d]"><ShieldCheck className="h-4 w-4" /> Private, disclosed storage</p>
            <p className="mt-2 text-[12px] leading-6 text-[#5f6d62]">Your name, email, exact age, raw answers, and calculated profile are stored for your report. Aggregate statistics require your separate optional consent. Regular users cannot inspect individual responses. Only the site owner and authorised administrators can do so through protected, audited server tools.</p>
          </div>
        </div>
        <form onSubmit={submitAssessment} className="rounded-[1.25rem] border border-[#dfccbd] bg-[#fffdf9] p-6 shadow-[0_24px_80px_rgba(68,49,36,.1)] sm:p-8">
          <h2 className="font-headline text-[2rem] text-[#2f2d2a]">Send my report</h2>
          <p className="mt-2 text-[12px] leading-5 text-[#756d66]">Result delivery is transactional. Book and journal updates are optional and separate.</p>
          <div className="mt-6 space-y-4">
            <label className="block text-[13px] font-semibold text-[#3d3834]">Name
              <input type="text" autoComplete="name" required minLength={2} maxLength={120} value={respondentName} onChange={(event) => setRespondentName(event.target.value)} className="mt-2 h-12 w-full rounded-[.55rem] border border-[#d6c5b8] bg-white px-4 text-[15px] outline-none transition focus:border-[#5f8769] focus:ring-2 focus:ring-[#5f8769]/20" placeholder="Your name" />
            </label>
            <label className="block text-[13px] font-semibold text-[#3d3834]">Email address
              <div className="relative mt-2">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a817a]" />
                <input type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} readOnly={Boolean(user?.email)} className="h-12 w-full rounded-[.55rem] border border-[#d6c5b8] bg-white pl-11 pr-4 text-[15px] outline-none transition read-only:bg-[#f6f1ec] focus:border-[#5f8769] focus:ring-2 focus:ring-[#5f8769]/20" placeholder="you@example.com" />
              </div>
            </label>
            <label className="block text-[13px] font-semibold text-[#3d3834]">Age
              <input type="number" inputMode="numeric" autoComplete="off" required min={18} max={120} step={1} value={age} onChange={(event) => setAge(event.target.value)} className="mt-2 h-12 w-full rounded-[.55rem] border border-[#d6c5b8] bg-white px-4 text-[15px] outline-none transition focus:border-[#5f8769] focus:ring-2 focus:ring-[#5f8769]/20" placeholder="Your age" />
              <span className="mt-1.5 block text-[10px] font-normal leading-4 text-[#817870]">Exact age is used for the age-specific statistics described in the privacy notice.</span>
            </label>
            <label aria-hidden className="absolute -left-[9999px]">Website<input tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} /></label>
            <ConsentCheck checked={podcastContactConsent} onChange={setPodcastContactConsent}>
              Optional: I am open to being contacted about participating in The Untangled Life dating podcast. This choice does not affect my report and can be withdrawn.
            </ConsentCheck>
            {podcastContactConsent ? (
              <div className="space-y-4 rounded-[.8rem] border border-[#dfd1c6] bg-[#faf5f0] p-4">
                <fieldset>
                  <legend className="text-[12px] font-semibold text-[#3d3834]">Preferred podcast contact</legend>
                  <div className="mt-3 flex flex-wrap gap-4 text-[12px] text-[#5d5650]">
                    <label className="flex items-center gap-2"><input type="radio" name="podcast-contact" checked={podcastContactPreference === 'email'} onChange={() => setPodcastContactPreference('email')} className="accent-[#5f8769]" /> Email</label>
                    <label className="flex items-center gap-2"><input type="radio" name="podcast-contact" checked={podcastContactPreference === 'phone'} onChange={() => setPodcastContactPreference('phone')} className="accent-[#5f8769]" /> Phone</label>
                  </div>
                </fieldset>
                {podcastContactPreference === 'phone' ? (
                  <label className="block text-[12px] font-semibold text-[#3d3834]">Phone number
                    <input type="tel" autoComplete="tel" required minLength={6} maxLength={40} value={podcastPhone} onChange={(event) => setPodcastPhone(event.target.value)} className="mt-2 h-11 w-full rounded-[.55rem] border border-[#d6c5b8] bg-white px-4 text-[14px] outline-none focus:border-[#5f8769] focus:ring-2 focus:ring-[#5f8769]/20" placeholder="Include country code" />
                  </label>
                ) : null}
                <label className="block text-[12px] font-semibold text-[#3d3834]">What would you be comfortable discussing? <span className="font-normal text-[#817870]">(optional)</span>
                  <textarea rows={4} maxLength={1500} value={podcastStory} onChange={(event) => setPodcastStory(event.target.value)} className="mt-2 w-full resize-y rounded-[.55rem] border border-[#d6c5b8] bg-white px-4 py-3 text-[14px] leading-6 outline-none focus:border-[#5f8769] focus:ring-2 focus:ring-[#5f8769]/20" placeholder="A short note for the owner/admin reviewing possible guests" />
                </label>
              </div>
            ) : null}
            <ConsentCheck checked={marketingConsent} onChange={setMarketingConsent}>
              Optional: email me occasional book releases and journal updates. I can unsubscribe at any time.
            </ConsentCheck>
          </div>
          {error ? <AssessmentError>{error}</AssessmentError> : null}
          <button type="submit" disabled={submitting} className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-[.6rem] bg-[#5f8769] px-5 text-[14px] font-semibold text-white shadow-[0_12px_28px_rgba(95,135,105,.24)] hover:bg-[#52775d] disabled:cursor-wait disabled:opacity-65">
            {submitting ? 'Calculating securely…' : 'Calculate and email my report'} {!submitting ? <ArrowRight className="h-4 w-4" /> : null}
          </button>
          <button type="button" onClick={() => { setPhase('questions'); setPageIndex(PAGE_COUNT - 1); }} className="mt-4 inline-flex w-full items-center justify-center gap-2 text-[12px] font-semibold text-[#6d655e] underline underline-offset-4"><ArrowLeft className="h-3.5 w-3.5" /> Review my answers</button>
          <button type="button" onClick={startOver} className="mt-4 text-xs underline">Start over for a new assessment</button>
          <p className="mt-5 text-[10px] leading-5 text-[#8a817a]">By submitting, you confirm the required storage, profiling, and statistics consent choices made at the start. Podcast contact and marketing remain optional, separate choices. See the <Link href="/assessment/privacy" className="underline">assessment privacy notice</Link>. This assessment is not diagnostic or clinical.</p>
        </form>
      </div>
    </AssessmentFrame>
  );

  return (
    <AssessmentFrame>
      <div className="mx-auto max-w-[900px] text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e5eee5] text-[#4e7659]"><Leaf className="h-7 w-7" /></div>
        <p className="mt-6 text-[11px] font-bold uppercase tracking-[.29em] text-[#b8735c]">Private Assessment</p>
        <h1 className="mt-4 font-headline text-[clamp(3.1rem,7vw,5.8rem)] leading-[.95] tracking-[-.04em] text-[#2f2d2a]">Your report is saved.</h1>
        <div className="mx-auto mt-8 max-w-[700px] rounded-[1rem] border border-[#dfcdbf] bg-white/75 p-5 text-[13px] leading-6 text-[#665f58]">
          {submission?.emailDelivery.status === 'sent'
            ? `Your report is saved. The email provider accepted the message to ${email}; inbox delivery is not confirmed.`
            : submission?.emailDelivery.status === 'pending'
              ? 'Your report is saved and the access email is queued; provider acceptance is not confirmed.'
              : submission?.emailDelivery.status === 'pending_configuration' ? 'Your report is saved. Email is blocked by missing configuration. Contact support; no email acceptance is confirmed.' : submission?.emailDelivery.status === 'policy_restricted' ? 'Your report is saved. Legacy email recovery is restricted. Contact support; a new send cannot safely be promised.' : 'Your report is saved. Email acceptance is not confirmed. Keep this attempt for retry or contact support.'}
          {' '}Your complete scores remain private behind your account.
        </div>
        {submission?.claimed ? (
          <Link href={`/account/assessment?assessment=${encodeURIComponent(submission.assessmentId)}`} className="mt-7 inline-flex h-12 items-center justify-center gap-2 rounded-[.6rem] bg-[#5f8769] px-7 text-[14px] font-semibold text-white shadow-[0_12px_28px_rgba(95,135,105,.22)]">Open my full report <ArrowRight className="h-4 w-4" /></Link>
        ) : (
          <p className="mt-7 text-sm leading-7 text-[#665f58]">Open the secure link in your email to create an account or sign in. Use and verify the same email address. If the message is unavailable, <Link href={ROUTES.contact} className="underline">contact support</Link>.</p>
        )}
        <p className="mt-5 text-[11px] text-[#847b73]">Use and verify the same email address: {email}</p>
        <button type="button" onClick={startOver} className="mt-4 text-xs underline">Start over for a new assessment</button>
      </div>
    </AssessmentFrame>
  );
}

function AssessmentFrame({ children, compact = false }: { children: React.ReactNode; compact?: boolean }) {
  return (
    <section className="relative min-h-[calc(100vh-5rem)] overflow-hidden bg-[#f8f2ec] px-4 py-8 sm:px-8 lg:px-10 lg:py-12">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_7%_8%,rgba(184,115,92,.12),transparent_24%),radial-gradient(circle_at_92%_3%,rgba(95,135,105,.13),transparent_25%)]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[.18] [background-image:url('data:image/svg+xml,%3Csvg_viewBox=%220_0_160_160%22_xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter_id=%22n%22%3E%3CfeTurbulence_type=%22fractalNoise%22_baseFrequency=%22.8%22_numOctaves=%222%22_stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect_width=%22100%25%22_height=%22100%25%22_filter=%22url(%23n)%22_opacity=%22.08%22/%3E%3C/svg%3E')]" />
      <div className={cn('relative mx-auto', compact ? 'max-w-[1240px]' : 'max-w-[1240px] rounded-[1.4rem] border border-white/70 bg-white/35 p-6 shadow-[0_35px_100px_rgba(68,49,36,.08)] backdrop-blur-[2px] sm:p-10 lg:p-14')}>{children}</div>
    </section>
  );
}

function IntroMetric({ icon: Icon, label, value }: { icon: typeof Clock3; label: string; value: string }) {
  return <div className="rounded-[.85rem] border border-[#dfcdbf] bg-white/65 p-4"><Icon className="h-5 w-5 text-[#5f8769]" /><p className="mt-3 text-[9px] font-bold uppercase tracking-[.2em] text-[#9b6b58]">{label}</p><p className="mt-1 text-[13px] font-semibold text-[#3b3733]">{value}</p></div>;
}

function ConsentCheck({ checked, onChange, children }: { checked: boolean; onChange: (checked: boolean) => void; children: React.ReactNode }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-[.7rem] border border-[#e0d1c5] bg-white/75 p-3.5 text-[12px] leading-5 text-[#5b5550] transition hover:border-[#bdcdbd]">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-[#bba99b] accent-[#5f8769]" />
      <span>{children}</span>
    </label>
  );
}

function AssessmentError({ children }: { children: React.ReactNode }) {
  return <p role="alert" className="mt-4 rounded-[.65rem] border border-red-200 bg-red-50 px-4 py-3 text-[12px] leading-5 text-red-700">{children}</p>;
}

function AssessmentLoading() {
  return <section className="flex min-h-[70vh] items-center justify-center bg-[#f8f2ec]"><p className="text-sm text-[#6d665f]">Restoring your private assessment…</p></section>;
}
